import { Command } from 'commander';
import chalk from 'chalk';
import { resolve } from 'path';
import { OutputFormatter, getJsonFlag } from '../lib/output.js';
import { handleError } from '../lib/errors.js';
import { MutationContext } from '../lib/mutation-context.js';
import {
  exploreCodebase,
  parseExtensions,
  parseExcludeDirs,
} from '../lib/explorer.js';
import type { ExploreOptions } from '../lib/explorer.js';

// =============================================================================
// Types
// =============================================================================

interface ExploreCommandOptions {
  path?: string;
  depth?: string;
  include?: string;
  exclude?: string;
  markersOnly?: boolean;
  minConfidence?: string;
}

// =============================================================================
// Command
// =============================================================================

// Default exclude dirs — extended to suppress common noise sources
const DEFAULT_EXCLUDE_DIRS = 'node_modules,dist,.git,build,.next,__pycache__,venv,.venv,.claude,scripts,__tests__,tests,fixtures,__mocks__,.github';

export function createExploreCommand(): Command {
  const explore = new Command('explore')
    .description('Scan codebase for prompts, datasets, and MutagenT markers')
    .option('-p, --path <dir>', 'Directory to scan', '.')
    .option('--depth <n>', 'Max directory depth', '10')
    .option('--include <glob>', 'Include file pattern', '**/*.{ts,js,py,tsx,jsx}')
    .option('--exclude <dirs>', 'Comma-separated directories to exclude', DEFAULT_EXCLUDE_DIRS)
    .option('--markers-only', 'Only find existing MutagenT markers')
    .option('--min-confidence <level>', 'Minimum confidence level to include in output (high|medium|low)', 'medium')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent explore
  ${chalk.dim('$')} mutagent explore --path ./src
  ${chalk.dim('$')} mutagent explore --include "**/*.{ts,py}" --depth 5
  ${chalk.dim('$')} mutagent explore --markers-only
  ${chalk.dim('$')} mutagent explore --json

Detection modes:
  ${chalk.dim('Heuristic')}  Template variables ({var} or {{var}}), prompt constants, schema definitions
  ${chalk.dim('Marker')}     MutagenT:START/END comment markers from previous uploads
  ${chalk.dim('Agents')}     Multi-turn / tool-calling patterns (informational — WIP)

${chalk.bold('Template variable delimiters (inferred per-file):')}
  ${chalk.dim('single')}    ${chalk.cyan('{variable}')}    MutagenT platform canonical; LangChain PromptTemplate
  ${chalk.dim('double')}    ${chalk.cyan('{{variable}}')}  Handlebars / Mustache / LiquidJS / Jinja2 / LangChain ChatPromptTemplate

  ${chalk.dim('Inference is per-file: majority of bracket style wins, tie goes to single')}
  ${chalk.dim('(platform canonical). Fenced code blocks are stripped first; {"json": ...} ')}
  ${chalk.dim('literals are skipped via JSON-key lookahead. See the mutagent-cli skill at ')}
  ${chalk.dim('concepts/prompt-variables.md for the full contract.')}

  ${chalk.dim('With --json, each discovered prompt carries a ')}${chalk.cyan('delimiter')}${chalk.dim(' field:')}
    ${chalk.dim('{ "file": "src/prompts/summarize.ts", "line": 12, "reason": "template-variable", "delimiter": "single" }')}

${chalk.dim('Results are saved to .mutagent/mutation-context.md for use by other commands.')}
${chalk.cyan('Conceptual help (prompt vs agent taxonomy, brace inference): .claude/skills/mutagent-cli/workflows/exploration.md')} ${chalk.dim('(install via mutagent skills install)')}
    `)
    .action((options: ExploreCommandOptions) => {
      const isJson = getJsonFlag(explore);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        const scanPath = resolve(options.path ?? '.');
        const depth = parseInt(options.depth ?? '10', 10);
        const extensions = parseExtensions(options.include ?? '**/*.{ts,js,py,tsx,jsx}');
        const excludeDirs = parseExcludeDirs(options.exclude ?? DEFAULT_EXCLUDE_DIRS);
        const markersOnly = options.markersOnly ?? false;
        const minConfidenceRaw = options.minConfidence ?? 'medium';
        const validConfidenceLevels = ['high', 'medium', 'low'] as const;
        type ConfidenceLevel = typeof validConfidenceLevels[number];
        const minConfidence: ConfidenceLevel = validConfidenceLevels.includes(minConfidenceRaw as ConfidenceLevel)
          ? (minConfidenceRaw as ConfidenceLevel)
          : 'medium';

        const confidenceRank: Record<ConfidenceLevel, number> = { high: 2, medium: 1, low: 0 };
        const filterByConfidence = <T extends { confidence: ConfidenceLevel }>(items: T[]): T[] =>
          items.filter(item => confidenceRank[item.confidence] >= confidenceRank[minConfidence]);

        const exploreOpts: ExploreOptions = {
          path: scanPath,
          depth,
          extensions,
          excludeDirs,
          markersOnly,
        };

        if (!isJson) {
          console.log(chalk.cyan(`\nScanning ${scanPath}...\n`));
        }

        const rawResult = exploreCodebase(exploreOpts);

        // Apply confidence filter
        const result = {
          ...rawResult,
          prompts: filterByConfidence(rawResult.prompts),
          agents: filterByConfidence(rawResult.agents),
        };

        // Save results to mutation context
        const ctx = MutationContext.load(scanPath);

        for (const prompt of result.prompts) {
          ctx.addDiscoveredPrompt(prompt.file, prompt.line, prompt.preview);
        }

        for (const dataset of result.datasets) {
          ctx.addDiscoveredDataset(dataset.file, dataset.name, dataset.items);
        }

        ctx.save();

        // Output results
        if (isJson) {
          output.output({
            prompts: result.prompts,
            datasets: result.datasets,
            markers: result.markers,
            agents: result.agents,
            agentsWip: result.agents.length > 0 ? {
              status: 'early-access',
              message: 'Agent detection is informational only. Agent optimization & evaluation are WIP.',
              earlyAccessUrl: 'https://www.mutagent.io/agents-partnership',
            } : undefined,
            summary: {
              totalPrompts: result.prompts.length,
              totalDatasets: result.datasets.length,
              totalMarkers: result.markers.length,
              totalAgents: result.agents.length,
            },
            contextFile: '.mutagent/mutation-context.md',
          });
        } else {
          // Summary header
          const totalFindings = result.prompts.length + result.datasets.length + result.markers.length + result.agents.length;

          if (totalFindings === 0) {
            output.info('No prompts, datasets, or markers found.');
            console.log(chalk.dim('\n  Tip: Create a prompt with template variables like {{input}} to get started.'));
            return;
          }

          // Prompts table
          if (result.prompts.length > 0) {
            console.log(chalk.bold(`  Prompts Found (${String(result.prompts.length)}):`));
            console.log();
            for (const p of result.prompts) {
              const confidenceTag = p.confidence === 'high' ? chalk.green('[high]')
                : p.confidence === 'medium' ? chalk.yellow('[medium]')
                : chalk.dim('[low]');
              const reasonTag = chalk.dim(`[${p.reason}]`);
              console.log(`    ${confidenceTag} ${chalk.green(p.file)}:${chalk.yellow(String(p.line))} ${reasonTag}`);
              console.log(`      ${chalk.dim(p.preview)}`);
            }
            console.log();
          }

          // Datasets table
          if (result.datasets.length > 0) {
            console.log(chalk.bold(`  Datasets Found (${String(result.datasets.length)}):`));
            console.log();
            for (const d of result.datasets) {
              console.log(`    ${chalk.green(d.file)} ${chalk.dim(`(${String(d.items)} items)`)}`);
            }
            console.log();
          }

          // Markers table
          if (result.markers.length > 0) {
            console.log(chalk.bold(`  MutagenT Markers (${String(result.markers.length)}):`));
            console.log();
            for (const m of result.markers) {
              const idPart = m.platformId ? chalk.cyan(` id=${m.platformId}`) : '';
              console.log(`    ${chalk.green(m.file)}:${chalk.yellow(String(m.line))} ${chalk.magenta(m.type)}${idPart}`);
            }
            console.log();
          }

          // Agents table + WIP card
          if (result.agents.length > 0) {
            console.log(chalk.bold(`  Agents Detected (${String(result.agents.length)}):`));
            console.log();
            for (const a of result.agents) {
              const confidenceTag = a.confidence === 'high' ? chalk.green('[high]')
                : a.confidence === 'medium' ? chalk.yellow('[medium]')
                : chalk.dim('[low]');
              const reasonTag = chalk.dim(`[${a.reason}]`);
              console.log(`    ${confidenceTag} ${chalk.green(a.file)}:${chalk.yellow(String(a.line))} ${reasonTag}`);
              console.log(`      ${chalk.dim(a.preview)}`);
            }
            console.log();
            // WIP card
            console.log(chalk.yellow('  ┌─ Agents: Early Access / WIP ──────────────────────────────'));
            console.log(chalk.yellow('  │ ') + chalk.bold('Agent optimization & evaluation are actively in development.'));
            console.log(chalk.yellow('  │ ') + chalk.dim('Detected patterns above are informational only.'));
            console.log(chalk.yellow('  │ ') + chalk.dim('The Prompt path (single-shot + output schema) is production-ready.'));
            console.log(chalk.yellow('  │ ') + chalk.dim('Join early access for the Agent path:'));
            console.log(chalk.yellow('  │ ') + chalk.cyan('https://www.mutagent.io/agents-partnership'));
            console.log(chalk.yellow('  └────────────────────────────────────────────────────────────'));
            console.log();
          }

          // Summary
          console.log(chalk.dim('  ─────────────────────────────────'));
          console.log(`  ${chalk.bold('Summary:')} ${String(result.prompts.length)} prompts, ${String(result.datasets.length)} datasets, ${String(result.markers.length)} markers, ${String(result.agents.length)} agents`);
          console.log(chalk.dim(`  Saved to .mutagent/mutation-context.md`));
          console.log();
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });

  return explore;
}
